import { useState, useEffect } from "react";
import "./FloatingIcons.css";
import Tag from "./Tag";

import blueGrid from "/assets/Blue-grid.svg";
import greenGrid from "/assets/Green-grid.svg";
import tealGrid from "/assets/Teal-grid.svg";
import yellowGrid from "/assets/Yellow-grid.svg";
import {
  FlashcardFeatureIcon,
  HighlightFeatureIcon,
  StudyFeatureIcon,
  QuizFeatureIcon,
} from "./FeatureIcons";
import { cn } from "@/lib/utils";

export const video1url =
  "https://kaqfkb3wglm1xmx8.public.blob.vercel-storage.com/assets/video-1-x6hvjSxVRBey80nlv74dypeFCX0Chd.mp4";
export const video2url =
  "https://kaqfkb3wglm1xmx8.public.blob.vercel-storage.com/assets/video-2-5wkGMnAVkoTRG0k7m6jmqMmcf3TBag.mp4";
export const video3url =
  "https://kaqfkb3wglm1xmx8.public.blob.vercel-storage.com/assets/video-3-OoKkgmFbHfFhh1iV2dYCyXIVPcsDbp.mp4";
export const video4url =
  "https://kaqfkb3wglm1xmx8.public.blob.vercel-storage.com/assets/video-4-3hOXM7n7proxMcqN3MuahNeCTEu7Ug.mp4";

const FloatingIcons = ({
  onContentChange,
}: {
  onContentChange: (
    media: string,
    title: string,
    description: string,
    videoClass?: string
  ) => void;
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [autoSwitch, setAutoSwitch] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const highlightText = (text: string) =>
    text
      .replace(/in seconds\./, '<span class="yellow-text">in seconds.</span>')
      .replace(/understand/, '<span class="teal-text">understand</span>')
      .replace(/efficient/, '<span class="blue-text">efficient</span>')
      .replace(/Quizzes/, '<span class="green-text">Quizzes</span>');

  const icons = [
    {
      src: FlashcardFeatureIcon,
      media: video1url,
      title: highlightText(
        isMobile
          ? "Generate flashcards in seconds."
          : "Generate quality flashcards in seconds."
      ),
      description: "Upload your notes and start generating flashcards",
      tagText: "Flashcards",
      tagVariant: "yellow" as const,
      videoClass: "yellow-video" as const,
      background: yellowGrid,
    },
    {
      src: HighlightFeatureIcon,
      media: video2url,
      title: highlightText(
        isMobile
          ? "Highlight & understand your notes."
          : "Highlight and understand your notes."
      ),
      description: "Upload your notes and interact with them",
      tagText: "Magic Highlight",
      tagVariant: "teal" as const,
      videoClass: "teal-video" as const,
      background: tealGrid,
    },
    {
      src: StudyFeatureIcon,
      media: video3url,
      title: highlightText(
        isMobile
          ? "Study with an efficient method."
          : "Study in the most efficient way."
      ),
      description: "Study your flashcards with the best algorithms",
      tagText: "Spaced Repetition",
      tagVariant: "blue" as const,
      videoClass: "blue-video" as const,
      background: blueGrid,
    },
    {
      src: QuizFeatureIcon,
      media: video4url,
      title: highlightText(
        isMobile
          ? "Generate infinite Quizzes."
          : "Generate quality Quizzes instantly."
      ),
      description: "Test what you've got before your next exam",
      tagText: "Quizzes",
      tagVariant: "green" as const,
      videoClass: "green-video" as const,
      background: greenGrid,
    },
  ];

  useEffect(() => {
    onContentChange(
      icons[activeIndex].media,
      icons[activeIndex].title,
      icons[activeIndex].description,
      icons[activeIndex].videoClass
    );
  }, [activeIndex, isMobile]);

  useEffect(() => {
    if (!autoSwitch) return;

    const interval = setInterval(() => {
      setActiveIndex((prevIndex) => (prevIndex + 1) % icons.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [autoSwitch]);

  return (
    <div className="container">
      {icons.map((icon, index) => (
        <div
          key={index}
          className={`icon-container ${activeIndex === index ? "active" : ""}`}
          onClick={() => {
            onContentChange(icon.media, icon.title, icon.description);
            setActiveIndex(index);
            setAutoSwitch(false);
          }}
        >
          {/* Background SVG - Only visible when active */}

          {activeIndex === index && (
            <img
              src={icon.background}
              alt="background"
              className="icon-background"
            />
          )}

          <Tag
            variant={icon.tagVariant}
            size="floating"
            className={`floating-tag ${activeIndex === index ? "active" : ""}`}
          >
            {icon.tagText}
          </Tag>

          <icon.src
            className={cn(
              "scale-125 z-50 opacity-60 hover:opacity-100",
              activeIndex === index && "opacity-100 active"
            )}
          />
        </div>
      ))}
    </div>
  );
};

export default FloatingIcons;
